import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { EmailCodeSignIn } from "@dolfin/ui";
import { useAuth, type OAuthProvider } from "../auth/AuthContext";
import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { useTheme } from "@/components/ThemeProvider";
import { useBrand } from "@/branding/BrandProvider";
import { AuthPanel } from "./LoginPage";

export function RegisterPage() {
  const { isAuthenticated, register } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const brand = useBrand();
  const logo = theme === "dark" ? brand.logoDark : brand.logoLight;

  const magicLinkToken = searchParams.get("token");

  // Resolves to an error to show, or null once joined (or already a member).
  const join = async (accessToken: string, provider: OAuthProvider): Promise<string | null> => {
    setError(null);
    setJoining(true);
    const result = await register(accessToken, magicLinkToken!, provider);
    setJoining(false);
    if (result.error && result.status !== 409) return result.error;
    if (result.error) toast.error("You're already a member of this organisation.");
    else toast.success("You've joined the organisation.");
    navigate("/", { replace: true });
    return null;
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => setError(await join(tokenResponse.access_token, "google")),
    onError: () => {
      setError("Google sign-in failed. Please try again.");
      setJoining(false);
    },
  });

  if (!magicLinkToken) {
    return (
      <AuthPanel
        logo={logo}
        brandName={brand.name}
        title="Invalid invite link"
        description="This link is missing an invite token. Contact hello@dolfinai.co for a new invite."
      />
    );
  }

  const emailOption = (
    <EmailCodeSignIn magicLinkToken={magicLinkToken} onProof={(proof) => join(proof, "email")} />
  );

  if (isAuthenticated) {
    return (
      <AuthPanel
        logo={logo}
        brandName={brand.name}
        title="Join organisation"
        description="You've been invited to join a new organisation. Confirm with Google or your email to continue."
        error={error}
      >
        <Button variant="outline" size="lg" className="w-full" disabled={joining} onClick={() => googleLogin()}>
          <GoogleIcon />
          {joining ? "Joining..." : "Confirm with Google"}
        </Button>
        {emailOption}
      </AuthPanel>
    );
  }

  return (
    <AuthPanel
      logo={logo}
      brandName={brand.name}
      title={`Welcome to ${brand.name}`}
      description="Sign in with Google or your email to create your account"
      error={error}
    >
      <Button variant="outline" size="lg" className="w-full" onClick={() => googleLogin()}>
        <GoogleIcon />
        Continue with Google
      </Button>
      {emailOption}
    </AuthPanel>
  );
}
